import { z } from 'zod';

/* The two MRI field sets the platform publishes: one for new service additions
   or changes, one for customer terminations. Each set carries an ordered list
   of fields an operator fills in when raising a case of that kind.

   Mirrors `catalogAdmin.ts` in shape — Zod for the form contract, plain TS
   types for the rest of the app — but with no `values` per field (the platform
   set has no enumerated values, only a name and a constraint) and no lifecycle:
   a set is active or superseded, and that is the service's business, not the
   form's. */

/** Which of the two sets a field belongs to. The string is the one the API
    carries; the form never spells it differently. */
export const MRI_FIELD_SET_KINDS = ['full', 'termination'] as const;
export type MriFieldSetKind = (typeof MRI_FIELD_SET_KINDS)[number];

/** The six MRI dimensions, shared with the catalog. Spelled here rather than
    imported from `serviceCatalog` so this file is self-contained for the form. */
const dimension = z.enum(['Who', 'What', 'Why', 'Where', 'When', 'How']);
export type MriDimension = z.infer<typeof dimension>;

/** The value types a field may carry. The same set the catalog allows. */
const valueType = z.enum([
  'String',
  'Integer',
  'Decimal',
  'Boolean',
  'Date',
  'DateTime',
  'GeographicSiteRef',
  'CustomerRef',
  'RequesterRef',
]);
export type MriValueType = z.infer<typeof valueType>;

/** One field's draft as the form holds it before submit. Numbers come in as
    strings from ScaleTextField, so the schema parses them; the service receives
    them as numbers. */
export const mriFieldDraftSchema = z
  .object({
    name: z.string().trim().min(1),
    businessName: z.string().trim().min(1).optional(),
    valueType,
    minCardinality: z.coerce.number().int().nonnegative(),
    maxCardinality: z.coerce.number().int().positive().optional(),
    mriDimension: dimension,
    confidenceFloor: z.coerce.number().min(0).max(1).optional(),
    conditionalRuleKey: z.string().trim().min(1).optional(),
    validationRuleKey: z.string().trim().min(1).optional(),
    displayOrder: z.coerce.number().int().nonnegative().optional(),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.maxCardinality !== undefined && value.maxCardinality < value.minCardinality) {
      context.addIssue({
        code: 'custom',
        path: ['maxCardinality'],
        message: 'maxCardinality must be at least minCardinality',
      });
    }
  });

export type MriFieldDraft = z.infer<typeof mriFieldDraftSchema>;

export const MRI_REQUIRED_CHOICES = ['required', 'optional'] as const;
export type MriRequiredChoice = (typeof MRI_REQUIRED_CHOICES)[number];

export function minCardinalityState(value: number): MriRequiredChoice {
  return value >= 1 ? 'required' : 'optional';
}

export function minCardinalityFromState(choice: MriRequiredChoice): number {
  return choice === 'required' ? 1 : 0;
}

/** Convert a persisted field into the draft shape the service accepts when a
    caller is replacing the entire active set. This centralises the `minCardinality`
    mapping and keeps the UI from inventing a second requiredness field. */
export function mriFieldDraftFromAdmin(field: MriFieldAdmin): MriFieldDraft {
  const draft: Record<string, unknown> = {
    name: field.name,
    valueType: field.valueType,
    minCardinality: field.minCardinality,
    mriDimension: field.mriDimension,
  };

  if (field.businessName !== null) draft.businessName = field.businessName;
  if (field.maxCardinality !== null) draft.maxCardinality = field.maxCardinality;
  if (field.confidenceFloor !== null) draft.confidenceFloor = field.confidenceFloor;
  if (field.conditionalRuleKey !== null) draft.conditionalRuleKey = field.conditionalRuleKey;
  if (field.validationRuleKey !== null) draft.validationRuleKey = field.validationRuleKey;
  if (field.displayOrder !== null) draft.displayOrder = field.displayOrder;

  return mriFieldDraftSchema.parse(draft);
}

/** The draft for a full set create/replace. The service accepts fields inline;
    the form sends them as a list. */
export const mriFieldSetDraftSchema = z
  .object({
    setKind: z.enum(MRI_FIELD_SET_KINDS),
    name: z.string().trim().min(1),
    fields: z.array(mriFieldDraftSchema).default([]),
  })
  .strict();

export type MriFieldSetDraft = z.infer<typeof mriFieldSetDraftSchema>;

/** A patch to a set's own columns (rename only). */
export const mriFieldSetPatchSchema = z
  .object({
    name: z.string().trim().min(1).optional(),
  })
  .strict();

export type MriFieldSetPatch = z.infer<typeof mriFieldSetPatchSchema>;

/** One field as the admin API returns it. Numbers stay as numbers; the
    confidenceFloor is a string from the wire because the service serialises
    Decimal that way, and is parsed to a number only where the form needs it. */
export interface MriFieldAdmin {
  readonly id: string;
  readonly setId: string;
  readonly name: string;
  readonly businessName: string | null;
  readonly valueType: MriValueType;
  readonly minCardinality: number;
  readonly maxCardinality: number | null;
  readonly mriDimension: MriDimension;
  readonly confidenceFloor: number | null;
  readonly conditionalRuleKey: string | null;
  readonly validationRuleKey: string | null;
  readonly displayOrder: number | null;
  readonly validForStart: string;
  readonly validForEnd: string | null;
}

/** One set as the admin API returns it. */
export interface MriFieldSetAdmin {
  readonly id: string;
  readonly setKind: MriFieldSetKind;
  readonly name: string;
  readonly status: string;
  readonly versionNo: number;
  readonly validForStart: string;
  readonly validForEnd: string | null;
  readonly fields: readonly MriFieldAdmin[];
}

export const MRI_DIMENSIONS: readonly MriDimension[] = [
  'Who',
  'What',
  'Why',
  'Where',
  'When',
  'How',
];

export const MRI_VALUE_TYPES: readonly MriValueType[] = [
  'String',
  'Integer',
  'Decimal',
  'Boolean',
  'Date',
  'DateTime',
  'GeographicSiteRef',
  'CustomerRef',
  'RequesterRef',
];

/** Parse a wire dimension string to the enum, falling back to `What` for an
    unknown value. The service only ever sends the six known strings, so the
    fallback is for safety rather than for a real case. */
export function mriDimension(value: string): MriDimension {
  return MRI_DIMENSIONS.find((candidate) => candidate === value) ?? 'What';
}

/** Parse a wire value-type string to the enum, falling back to `String`. */
export function mriValueType(value: string): MriValueType {
  return MRI_VALUE_TYPES.find((candidate) => candidate === value) ?? 'String';
}

/** Parse a wire set-kind string to the enum. The service only ever sends
    `full` or `termination`; the fallback keeps the type honest rather than
    representing a real case. */
export function mriFieldSetKind(value: string): MriFieldSetKind {
  return MRI_FIELD_SET_KINDS.find((candidate) => candidate === value) ?? 'full';
}

/* The selection rule: which set a case is asked against, given the order item's
   action. This is the documented contract between the case pipeline and the
   field sets — `add` and `modify` are a new service or a change to one, so the
   Full set applies; `terminate` is a customer termination, so the Termination
   set applies. Captured here even though the editor does not wire it into
   extraction yet (the plan defers that), because one place that owns the rule
   is better than two that would drift. The `ProductActionType` literal is the
   one TMF622 carries on the wire, spelled here so the case module does not have
   to be imported for a constant the form never reads. */
export type ProductOrderItemAction = 'add' | 'modify' | 'terminate';

export function actionToSetKind(action: ProductOrderItemAction): MriFieldSetKind {
  return action === 'terminate' ? 'termination' : 'full';
}
